import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Flame, Plus, Trash2, X, Camera, Utensils, Pencil, Sparkles, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

/**
 * Running daily calorie + macro tally.
 * Aggregates every "I ate this" from the meal plan, every food snap, and any
 * manual add. Sits on the Nutrition tab so users can see totals build up as
 * they log food.
 */
const Bar = ({ label, value, target, unit = 'g', color }) => {
  const pct = target > 0 ? Math.min(150, Math.round((value / target) * 100)) : 0;
  const over = value > target;
  const barWidth = Math.min(100, pct);
  return (
    <div data-testid={`macro-bar-${label.toLowerCase()}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider font-black text-zinc-400">{label}</span>
        <span className="text-[11px] tabular-nums text-zinc-300">
          <span className={over ? 'text-[#ff7a1a] font-bold' : 'text-white font-bold'}>{value}</span>
          <span className="text-zinc-500"> / {target}{unit}</span>
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${barWidth}%`, background: over ? '#ff7a1a' : color }}
        />
      </div>
    </div>
  );
};

const emptyEntry = { name: '', portion: '1 serving', calories: '', protein_g: '', carbs_g: '', fats_g: '' };

const DailyMacroTracker = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyEntry);
  const [saving, setSaving] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookedUp, setLookedUp] = useState(false);   // flip true once auto-filled so we show the "AI" hint

  const refresh = useCallback(async () => {
    setBusy(true);
    try {
      const d = await api.todayFoodLog();
      setData(d);
    } catch (e) {
      // silent — will retry on next open
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-refresh when a meal-plan "I ate this" fires on the same page.
  useEffect(() => {
    const onLogged = () => refresh();
    window.addEventListener('freefitfuel:food-logged', onLogged);
    return () => window.removeEventListener('freefitfuel:food-logged', onLogged);
  }, [refresh]);

  const remaining = useMemo(() => {
    if (!data) return null;
    return {
      calories: Math.max(0, data.targets.calories - data.totals.calories),
      protein_g: Math.max(0, data.targets.protein_g - data.totals.protein_g),
    };
  }, [data]);

  const lookupMacros = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Type the food first', description: 'e.g. "banana", "chicken sandwich", "flat white"', variant: 'destructive' });
      return;
    }
    setLookingUp(true);
    try {
      const r = await api.lookupFoodMacros(form.name.trim(), form.portion.trim() || '1 serving');
      setForm({
        name: form.name,
        portion: r.portion || form.portion,
        calories: String(r.calories),
        protein_g: String(r.protein_g),
        carbs_g: String(r.carbs_g),
        fats_g: String(r.fats_g),
      });
      setLookedUp(true);
      toast({
        title: r.cached ? 'Filled from cache' : 'Estimated',
        description: `${r.calories} kcal · P${r.protein_g} · C${r.carbs_g} · F${r.fats_g} — edit if you disagree.`,
      });
    } catch (err) {
      toast({ title: 'Lookup failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLookingUp(false);
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.calories) {
      toast({ title: 'Missing info', description: 'Give it a name and calorie count', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      await api.addManualFood({
        name: form.name.trim(),
        portion: form.portion.trim() || '1 serving',
        calories: Number(form.calories) || 0,
        protein_g: Number(form.protein_g) || 0,
        carbs_g: Number(form.carbs_g) || 0,
        fats_g: Number(form.fats_g) || 0,
      });
      setForm(emptyEntry);
      setShowAdd(false);
      setLookedUp(false);
      await refresh();
      toast({ title: 'Logged', description: `${form.name} added to today` });
    } catch (err) {
      toast({ title: 'Could not log', description: err?.message || 'Try again', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    try {
      await api.deleteFoodEntry(id);
      setData((d) => (d ? {
        ...d,
        entries: d.entries.filter((e) => e.id !== id),
        totals: d.entries.filter((e) => e.id === id).reduce((acc, e) => ({
          calories: acc.calories - e.calories,
          protein_g: acc.protein_g - e.protein_g,
          carbs_g: acc.carbs_g - e.carbs_g,
          fats_g: acc.fats_g - e.fats_g,
        }), d.totals),
      } : d));
    } catch (err) {
      toast({ title: 'Could not remove', description: err?.message || 'Try again', variant: 'destructive' });
    }
  };

  if (busy && !data) {
    return (
      <div className="rounded-xl border border-zinc-800 bg-[#0b0c10] p-4 text-center text-zinc-500 text-sm" data-testid="daily-tracker-loading">
        Loading today&apos;s macros…
      </div>
    );
  }
  if (!data) return null;

  const kcalPct = data.targets.calories > 0 ? Math.min(150, Math.round((data.totals.calories / data.targets.calories) * 100)) : 0;
  const kcalOver = data.totals.calories > data.targets.calories;

  return (
    <div className="rounded-xl border border-zinc-800 bg-[#0b0c10] p-4 space-y-4" data-testid="daily-macro-tracker">
      {/* Big calorie ring / count */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wider font-black text-zinc-500">Today&apos;s macros</p>
          <div className="flex items-baseline gap-2 mt-0.5">
            <span
              className={`font-black text-3xl tabular-nums ${kcalOver ? 'text-[#ff7a1a]' : 'text-[#BEFF38]'}`}
              data-testid="daily-total-kcal"
            >
              {data.totals.calories}
            </span>
            <span className="text-zinc-500 text-sm tabular-nums">
              / {data.targets.calories} kcal
            </span>
          </div>
          {remaining && !kcalOver && (
            <p className="text-[11px] text-zinc-500 mt-1">
              <span className="text-white font-bold tabular-nums">{remaining.calories}</span> kcal left ·{' '}
              <span className="text-white font-bold tabular-nums">{remaining.protein_g}</span>g protein to go
            </p>
          )}
          {kcalOver && (
            <p className="text-[11px] text-[#ff7a1a] mt-1">
              <span className="font-bold">+{data.totals.calories - data.targets.calories}</span> kcal over target
            </p>
          )}
        </div>
        <div className="relative w-16 h-16 shrink-0">
          <svg viewBox="0 0 40 40" className="w-full h-full -rotate-90">
            <circle cx="20" cy="20" r="17" fill="none" stroke="#1f2937" strokeWidth="4" />
            <circle
              cx="20" cy="20" r="17" fill="none"
              stroke={kcalOver ? '#ff7a1a' : '#BEFF38'}
              strokeWidth="4"
              strokeDasharray={2 * Math.PI * 17}
              strokeDashoffset={2 * Math.PI * 17 * (1 - Math.min(1, kcalPct / 100))}
              strokeLinecap="round"
              className="transition-all duration-500"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <Flame className="w-5 h-5 text-[#ff7a1a]" />
          </div>
        </div>
      </div>

      {/* Macro bars */}
      <div className="space-y-2.5">
        <Bar label="Protein" value={data.totals.protein_g} target={data.targets.protein_g} color="#38bdf8" />
        <Bar label="Carbs"   value={data.totals.carbs_g}   target={data.targets.carbs_g}   color="#a78bfa" />
        <Bar label="Fats"    value={data.totals.fats_g}    target={data.targets.fats_g}    color="#fbbf24" />
      </div>

      {/* Entries + add button */}
      <div className="pt-2 border-t border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider font-black text-zinc-400">
            Logged today · <span className="text-white">{data.entries.length}</span>
          </p>
          <button
            type="button"
            onClick={() => setShowAdd(true)}
            data-testid="daily-add-manual"
            className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#BEFF38] hover:text-white"
          >
            <Plus className="w-3 h-3" /> Add food
          </button>
        </div>
        {data.entries.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic">
            Nothing logged yet. Tap &ldquo;I ate this&rdquo; on a meal-plan recipe, snap a photo, or add manually.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {data.entries.map((e) => {
              const Icon = e.source === 'meal-plan' ? Utensils : e.source === 'snap' ? Camera : Pencil;
              return (
                <li key={e.id} className="flex items-center gap-2 text-[12px] text-zinc-200 rounded-lg bg-zinc-900/50 border border-zinc-800 px-3 py-2" data-testid={`daily-entry-${e.id}`}>
                  <Icon className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate">{e.name}</p>
                    <p className="text-[10px] text-zinc-500 tabular-nums">
                      <span className="text-[#ff7a1a] font-bold">{e.calories} kcal</span>
                      {' · '}P {e.protein_g} · C {e.carbs_g} · F {e.fats_g}
                      {e.portion && ` · ${e.portion}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeEntry(e.id)}
                    className="text-zinc-500 hover:text-[#ff7a1a] p-1"
                    aria-label="Remove entry"
                    data-testid={`daily-entry-delete-${e.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Manual add sheet */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4 overflow-y-auto" onClick={() => { setShowAdd(false); setLookedUp(false); }}>
          <form
            onSubmit={submitManual}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl bg-[#0b0c10] border border-zinc-800 p-4 space-y-3 my-auto"
            data-testid="daily-add-form"
          >
            <div className="flex items-center justify-between">
              <p className="font-black text-white text-sm uppercase tracking-wider">Add food</p>
              <button type="button" onClick={() => { setShowAdd(false); setLookedUp(false); }} className="text-zinc-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <input
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setLookedUp(false); }}
              placeholder="What did you eat?"
              autoFocus
              data-testid="daily-add-name"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38]"
            />
            <input
              value={form.portion}
              onChange={(e) => { setForm({ ...form, portion: e.target.value }); setLookedUp(false); }}
              placeholder="Portion (e.g. 1 medium, 200g, 1 tin)"
              data-testid="daily-add-portion"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38]"
            />

            {/* AI look-up button — fills all four macro fields */}
            <button
              type="button"
              onClick={lookupMacros}
              disabled={lookingUp || !form.name.trim()}
              data-testid="daily-add-lookup"
              className="w-full inline-flex items-center justify-center gap-2 bg-sky-500/15 border border-sky-500/40 text-sky-300 font-black uppercase tracking-wider text-xs py-2 rounded-lg hover:bg-sky-500/25 disabled:opacity-50"
            >
              {lookingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {lookingUp ? 'Estimating…' : 'Auto-fill macros'}
            </button>
            {lookedUp && (
              <p className="text-[10px] text-sky-300/80 -mt-1">Estimated by AI — edit any field if you disagree.</p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 space-y-1 block">
                <span>Calories *</span>
                <input
                  type="number" inputMode="numeric" min="0" max="5000"
                  value={form.calories}
                  onChange={(e) => setForm({ ...form, calories: e.target.value })}
                  data-testid="daily-add-calories"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38] tabular-nums"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 space-y-1 block">
                <span>Protein (g)</span>
                <input
                  type="number" inputMode="numeric" min="0" max="500"
                  value={form.protein_g}
                  onChange={(e) => setForm({ ...form, protein_g: e.target.value })}
                  data-testid="daily-add-protein"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38] tabular-nums"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 space-y-1 block">
                <span>Carbs (g)</span>
                <input
                  type="number" inputMode="numeric" min="0" max="1000"
                  value={form.carbs_g}
                  onChange={(e) => setForm({ ...form, carbs_g: e.target.value })}
                  data-testid="daily-add-carbs"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38] tabular-nums"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-zinc-400 space-y-1 block">
                <span>Fats (g)</span>
                <input
                  type="number" inputMode="numeric" min="0" max="500"
                  value={form.fats_g}
                  onChange={(e) => setForm({ ...form, fats_g: e.target.value })}
                  data-testid="daily-add-fats"
                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-[#BEFF38] tabular-nums"
                />
              </label>
            </div>
            <button
              type="submit"
              disabled={saving}
              data-testid="daily-add-submit"
              className="w-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm py-2.5 rounded-lg hover:brightness-95 disabled:opacity-50"
            >
              {saving ? 'Adding…' : 'Add to today'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default DailyMacroTracker;
